import React, { useState, useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import ArticleEditor from "../../../../components/dashboardcomponents/KnowledgeBase/NewArticle/ArticleEditor";
import ArticleSideBar from "../../../../components/dashboardcomponents/KnowledgeBase/NewArticle/ArticleSideBar";
import KbTagPopup from "../../../../components/dashboardcomponents/KnowledgeBase/NewArticle/KbTagPopup";
import {
    getKbArticleById,
    resetArticleDetail,
} from "../../../../store/features/getKbArticleById";
import {
    updateKbArticle,
    resetUpdateState,
} from "../../../../store/features/updateKbArticle";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { toast } from "react-toastify";
import ArticleDetailSkeleton from "../../../../components/dashboardcomponents/KnowledgeBase/Articles/ArticleDetailSkeleton";
import { markNotificationsReadForContext } from "../../../../store/features/notifications";
import { getKbGroupId, setKbGroupId } from "../../../../utils/kbGroupId";

const normalizeCategoryId = (value) => {
    if (value == null || value === "") return "";
    if (typeof value === "object") return String(value._id || value.id || "");
    return String(value);
};

const formatArticleDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

const getUserDisplayName = (user) => {
    if (!user) return "";
    if (typeof user === "string") return "";
    return (
        user.full_name ||
        user.name ||
        [user.first_name, user.last_name].filter(Boolean).join(" ") ||
        user.email ||
        ""
    );
};

const ArticleDetail = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { articleId } = useParams();

    const { article } = useSelector(
        (state) => state.kbArticleDetail
    );
    const groupId = article?.group_id || getKbGroupId();
    const articleMatchesRoute =
        Boolean(article) &&
        Boolean(articleId) &&
        articleId !== "new" &&
        (String(article._id) === String(articleId) ||
            (article.slug && String(article.slug) === String(articleId)));
    const {
        loading: updateLoading,
        success: updateSuccess,
        error: updateError,
    } = useSelector((state) => state.updateKbArticle);
    const business_id = useSelector(
        (state) => state.loginToken?.response?.user?.business_id
    );
    const kbRole = useSelector(
        (state) => state.loginToken?.response?.user?.kbRole?.role
    );

    // Viewers should not be able to edit articles
    const isReadOnly = kbRole === 'viewer';

    const [articleData, setArticleData] = useState({
        title: "",
        content: "<p></p>",
        mode: "edit",
        createdDate: "",
        lastDateEdit: "",
        lastEditBy: "",
        author: "",
        categoryIds: [],
        visibility: "public",
        tags: [],
        slug: "",
        comments: [],
        disableComments: false,
    });

    const [showTagPopup, setShowTagPopup] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hydratedKey, setHydratedKey] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useLayoutEffect(() => {
        if (!articleId || articleId === "new") return undefined;
        setIsInitialized(false);
        setHydratedKey(null);
        dispatch(getKbArticleById({ articleId }));

        return () => {
            dispatch(resetArticleDetail());
            dispatch(resetUpdateState());
        };
    }, [dispatch, articleId]);

    useEffect(() => {
        if (!articleId || articleId === "new") return;
        dispatch(markNotificationsReadForContext({ articleId }));
    }, [dispatch, articleId]);

    useEffect(() => {
        if (article?.group_id) {
            setKbGroupId(article.group_id);
        }
    }, [article?.group_id]);

    useEffect(() => {
        if (!article || !articleMatchesRoute) return;

        // Re-hydrate whenever server article identity/version changes (fixes stale cache → locked local state).
        const nextKey = `${article._id || articleId}:${article.updatedAt || article.lastEditedAt || ""}`;
        if (hydratedKey === nextKey) return;

        setArticleData({
            _id: article._id || articleId,
            title: article.title || "",
            content: article.content || "<p></p>",
            mode: "edit",
            createdDate: formatArticleDate(article.createdAt),
            lastDateEdit: formatArticleDate(article.lastEditedAt),
            lastEditBy: getUserDisplayName(article.lastEditedBy),
            author: getUserDisplayName(article.authorId),
            // Keep populated {_id, name} so the sidebar can show the category
            // even before / if the categories list hasn't loaded yet.
            categoryIds: (article.categoryIds || [])
                .map((c) => {
                    if (c && typeof c === "object") {
                        return c._id
                            ? { _id: String(c._id), name: c.name || "" }
                            : null;
                    }
                    return c ? String(c) : null;
                })
                .filter(Boolean),
            visibility: article.visibility || "public",
            tags: article.tagIds || [],
            slug: article.slug || "",
            status: article.status || "draft",
            comments: article.feedbacks ? article.feedbacks
                .filter(feedback => feedback.source === 'internal')
                .flatMap(feedback =>
                    (feedback.comments || []).map(c => ({
                        id: c._id,
                        feedbackId: feedback._id,
                        author: c.authorId?.full_name || "Unknown",
                        avatar: c.authorId?.agent_image || "",
                        date: new Date(c.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                        }),
                        content: c.comment,
                        user_id: c.authorId?._id,
                    }))
                ) : [],
            disableComments: article.disableComments ?? false,
        });
        setHydratedKey(nextKey);
        setIsInitialized(true);
    }, [article, articleMatchesRoute, articleId, hydratedKey]);

    // PUT responses often omit populated author/lastEditedBy. When GET (or a
    // later payload) fills them in, keep the Info sidebar in sync without
    // resetting in-progress title/content edits.
    useEffect(() => {
        if (!article || !articleMatchesRoute || !isInitialized) return;

        const lastEditBy = getUserDisplayName(article.lastEditedBy);
        const author = getUserDisplayName(article.authorId);
        const lastDateEdit = formatArticleDate(article.lastEditedAt);

        setArticleData((prev) => {
            if (
                prev.lastEditBy === lastEditBy &&
                prev.author === author &&
                prev.lastDateEdit === (lastDateEdit || prev.lastDateEdit)
            ) {
                return prev;
            }
            return {
                ...prev,
                lastEditBy,
                author,
                lastDateEdit: lastDateEdit || prev.lastDateEdit,
            };
        });
    }, [
        article,
        articleMatchesRoute,
        isInitialized,
        article?.lastEditedBy,
        article?.authorId,
        article?.lastEditedAt,
    ]);

    // Handle update success/error
    useEffect(() => {
        if (isSaving && updateSuccess) {
            toast.success("Article updated successfully!");
            dispatch(resetUpdateState());
            setIsSaving(false);
            dispatch(getKbArticleById({ articleId }));
            navigate("/kb/articles");
        }
        if (isSaving && updateError) {
            toast.error(updateError?.message || "Failed to update article");
            dispatch(resetUpdateState());
            setIsSaving(false);
        }
    }, [updateSuccess, updateError, isSaving, dispatch, navigate]);


    useEffect(() => {
        if (!article || !isInitialized) return;

        const areArraysEqual = (arr1, arr2) => {
            if (!arr1 || !arr2) return arr1 === arr2;
            if (arr1.length !== arr2.length) return false;
            const sorted1 = [...arr1].sort();
            const sorted2 = [...arr2].sort();
            return sorted1.every((val, index) => val === sorted2[index]);
        };

        const currentTags = articleData.tags.map(t => t._id || t.id).filter(Boolean);
        const initialTags = (article.tagIds || []).map(t => (typeof t === 'object' ? t._id : t)).filter(Boolean);

        const currentCategories = (articleData.categoryIds || [])
            .map(normalizeCategoryId)
            .filter(Boolean);
        const initialCategories = (article.categoryIds || [])
            .map(normalizeCategoryId)
            .filter(Boolean);

        const isTitleChanged = articleData.title !== (article.title || "");
        const isContentChanged = articleData.content !== (article.content || "<p></p>");
        const isVisibilityChanged = articleData.visibility !== (article.visibility || "public");
        const isDisableCommentsChanged = articleData.disableComments !== (article.disableComments ?? false);
        const areTagsChanged = !areArraysEqual(currentTags, initialTags);
        const areCategoriesChanged = !areArraysEqual(currentCategories, initialCategories);

        const isDirty = isTitleChanged || isContentChanged || isVisibilityChanged || isDisableCommentsChanged || areTagsChanged || areCategoriesChanged;

        window.dispatchEvent(new CustomEvent('ARTICLE_DIRTY_STATE', { detail: { isDirty } }));

    }, [articleData, article, isInitialized]);

    useEffect(() => {
        const handleSaveRequest = (event) => {
            const actionType = event.detail;
            if (actionType) {
                handleSaveAction(actionType);
            }
        };

        window.addEventListener("SAVE_ARTICLE_TRIGGER", handleSaveRequest);

        return () => {
            window.removeEventListener("SAVE_ARTICLE_TRIGGER", handleSaveRequest);
        };
    }, [articleData]);

    const setTitle = (newTitle) => {
        setArticleData((prev) => ({ ...prev, title: newTitle }));
    };

    const setContent = (newContent) => {
        setArticleData((prev) => ({ ...prev, content: newContent }));
    };

    const handleSaveAction = async (type) => {
        if (type === "cancel") {
            navigate("/kb/articles");
            return;
        }

        if (!articleData.title.trim()) {
            toast.error("Please enter a title for the article");
            return;
        }

        // Create API uses saveAction "publish"; update PUT uses status "published".
        const statusMap = {
            draft: "draft",
            submitForReview: "pendingReview",
            publish: "published",
            published: "published",
        };

        if (groupId) setKbGroupId(groupId);

        const payload = {
            articleId: articleId,
            title: articleData.title,
            content: articleData.content,
            categoryIds: (articleData.categoryIds || [])
                .map(normalizeCategoryId)
                .filter(Boolean),
            visibility: articleData.visibility,
            tagIds: articleData.tags.map((t) => t._id || t.id).filter((id) => id),
            status:
                type === "update"
                    ? article.status
                    : statusMap[type] ?? type,
            comments: articleData.comments,
            disableComments: articleData.disableComments,
            group_id: groupId,
        };

        setIsSaving(true);
        dispatch(updateKbArticle(payload));
    };

    // Skeleton only on first load / different article. Revisits use cache while refreshing in background.
    if (!articleMatchesRoute) {
        return (
            <ArticleDetailSkeleton />
        );
    }

    return (
        <div
            style={{
                display: "flex",
                height: "100vh",
                width: "100%",
                backgroundColor: "#fff",
                overflow: "hidden",
            }}
        >
            <div
                style={{ flex: 1, overflowY: "auto", overflowX: "hidden", minWidth: 0 }}
            >
                <ArticleEditor
                    title={articleData.title}
                    setTitle={setTitle}
                    content={articleData.content}
                    setContent={setContent}
                    isReadOnly={isReadOnly}
                />
            </div>
            <div
                style={{
                    width: "420px",
                    flexShrink: 0,
                    borderLeft: "1px solid #e5e7eb",
                    height: "100%",
                    overflowY: "auto",
                }}
            >
                <ArticleSideBar
                    articleData={articleData}
                    setArticleData={setArticleData}
                    onAddTag={() => setShowTagPopup(true)}
                    isReadOnly={isReadOnly}
                    groupId={groupId}
                />
            </div>
            {showTagPopup && (
                <KbTagPopup
                    onClose={() => setShowTagPopup(false)}
                    onApply={(newTags) => {
                        setArticleData((prev) => ({ ...prev, tags: newTags }));
                    }}
                    selectedTags={articleData.tags}
                />
            )}
        </div>
    );
};

export default ArticleDetail;
